import type { GrupoCopaId, JogoCopa } from '../data/copaMundo2026';
import {
  GRUPOS_COPA_2026,
  JOGOS_COPA_2026,
  jogosDoGrupoId,
  nomeTime,
} from '../data/copaMundo2026';
import {
  JOGOS_MATA_MATA,
  PONTUACAO_POR_FASE,
  type FaseMataMata,
  type JogoMataMata,
} from '../data/mataMataCopa2026';

export type Placar = { mandante: number; visitante: number };

export type PlacaresMap = Record<string, Placar | null | undefined>;

/** Lado que avança quando o placar do mata-mata termina empatado (pênaltis). */
export type LadoPlacarMM = 'mandante' | 'visitante';

export type VencedoresPenaltisMMMap = Record<string, LadoPlacarMM>;

export interface LinhaClassificacao {
  timeId: string;
  nome: string;
  pts: number;
  jogos: number;
  v: number;
  e: number;
  d: number;
  gp: number;
  gc: number;
  sg: number;
  posicao: number;
}

export type Resultado1x2 = 'M' | 'V' | 'E';

export function placarValido(p: Placar | null | undefined): p is Placar {
  if (!p) return false;
  const m = Number(p.mandante);
  const v = Number(p.visitante);
  return Number.isFinite(m) && Number.isFinite(v) && m >= 0 && v >= 0;
}

/** Jogos da fase de grupos (72) com palpite válido — ignora chaves extras no mapa. */
export function contarPalpitesGruposValidos(palpites: PlacaresMap): number {
  return JOGOS_COPA_2026.filter((j) => placarValido(palpites[j.id])).length;
}

export function resultado1x2(p: Placar): Resultado1x2 {
  if (p.mandante > p.visitante) return 'M';
  if (p.mandante < p.visitante) return 'V';
  return 'E';
}

export function placarEmpatadoMM(p: Placar | null | undefined): boolean {
  return placarValido(p) && p.mandante === p.visitante;
}

/** Quem avança no mata-mata (90 min ou pênaltis). */
export function vencedorRealMataMata(
  placar: Placar,
  vencedorPenaltis?: LadoPlacarMM | null
): LadoPlacarMM | null {
  if (!placarValido(placar)) return null;
  if (placar.mandante > placar.visitante) return 'mandante';
  if (placar.visitante > placar.mandante) return 'visitante';
  return vencedorPenaltis ?? null;
}

export function resultado1x2MataMata(
  p: Placar,
  vencedorPenaltis?: LadoPlacarMM | null
): Resultado1x2 {
  const v = vencedorRealMataMata(p, vencedorPenaltis);
  if (v === 'mandante') return 'M';
  if (v === 'visitante') return 'V';
  return 'E';
}

/**
 * Acerto de vencedor no mata-mata (sem contar placar exato).
 * Pontuação vale só pelo tempo normal (90 min): prorrogação e pênaltis não entram.
 * Se o real empatou nos 90 min e há vencedor de pênaltis marcado, conta acerto
 * para quem palpitou empate (ex.: real 1×1, palpite 2×2). Quem palpitou vitória
 * do time que avançou nos pênaltis não ganha os pontos de acerto parcial.
 */
export function acertouVencedorMataMata(
  palpite: Placar,
  real: Placar,
  vencedorPenaltisReal?: LadoPlacarMM | null
): boolean {
  if (!placarValido(palpite) || !placarValido(real)) return false;

  const resultadoReal90 = resultado1x2(real);
  const resultadoPalpite90 = resultado1x2(palpite);

  if (resultadoReal90 === 'E') {
    if (!vencedorPenaltisReal) return false;
    return resultadoPalpite90 === 'E';
  }

  return resultadoPalpite90 === resultadoReal90;
}

/** Placar exato = 3 pts; senão, acertou vitória/empate = 1 pt (fase de grupos) */
export function pontosJogo(palpite: Placar, real: Placar): number {
  if (palpite.mandante === real.mandante && palpite.visitante === real.visitante) return 3;
  return resultado1x2(palpite) === resultado1x2(real) ? 1 : 0;
}

/** Pontuação do mata-mata com multiplicador por fase (6/2, 9/3, 12/4, 15/5, 18/6). */
export function pontosJogoMataMata(
  palpite: Placar,
  real: Placar,
  fase: FaseMataMata,
  vencedorPenaltisReal?: LadoPlacarMM | null
): number {
  const p = PONTUACAO_POR_FASE[fase];
  if (palpite.mandante === real.mandante && palpite.visitante === real.visitante) {
    return p.placarExato;
  }
  return acertouVencedorMataMata(palpite, real, vencedorPenaltisReal) ? p.acertoVencedor : 0;
}

/** True se todos os 72 jogos da fase de grupos têm resultado real válido. */
export function fasesGruposEncerrada(reaisGrupos: PlacaresMap): boolean {
  return GRUPOS_COPA_2026.every((g) =>
    jogosDoGrupoId(g.id).every((j) => placarValido(reaisGrupos[j.id]))
  );
}

export function calcularClassificacaoGrupo(
  grupoId: GrupoCopaId,
  placares: PlacaresMap
): LinhaClassificacao[] {
  const grupo = GRUPOS_COPA_2026.find((g) => g.id === grupoId);
  if (!grupo) return [];

  const stats = new Map<
    string,
    Omit<LinhaClassificacao, 'posicao' | 'nome'>
  >();

  grupo.times.forEach((tm) => {
    stats.set(tm.id, {
      timeId: tm.id,
      pts: 0,
      jogos: 0,
      v: 0,
      e: 0,
      d: 0,
      gp: 0,
      gc: 0,
      sg: 0,
    });
  });

  jogosDoGrupoId(grupoId).forEach((jogo) => {
    const p = placares[jogo.id];
    if (!placarValido(p)) return;
    const m = stats.get(jogo.mandanteId);
    const v = stats.get(jogo.visitanteId);
    if (!m || !v) return;

    m.jogos += 1;
    v.jogos += 1;
    m.gp += p.mandante;
    m.gc += p.visitante;
    v.gp += p.visitante;
    v.gc += p.mandante;

    if (p.mandante > p.visitante) {
      m.pts += 3;
      m.v += 1;
      v.d += 1;
    } else if (p.mandante < p.visitante) {
      v.pts += 3;
      v.v += 1;
      m.d += 1;
    } else {
      m.pts += 1;
      v.pts += 1;
      m.e += 1;
      v.e += 1;
    }
  });

  const linhas = Array.from(stats.values()).map((s) => ({
    ...s,
    nome: nomeTime(s.timeId),
    sg: s.gp - s.gc,
    posicao: 0,
  }));

  linhas.sort((a, b) => {
    if (b.pts !== a.pts) return b.pts - a.pts;
    if (b.sg !== a.sg) return b.sg - a.sg;
    if (b.gp !== a.gp) return b.gp - a.gp;
    return a.nome.localeCompare(b.nome, 'pt-BR');
  });

  linhas.forEach((l, i) => {
    l.posicao = i + 1;
  });

  return linhas;
}

export function mapaPosicoesPorGrupo(placares: PlacaresMap): Record<GrupoCopaId, Record<string, number>> {
  const out = {} as Record<GrupoCopaId, Record<string, number>>;
  GRUPOS_COPA_2026.forEach((g) => {
    const tab = calcularClassificacaoGrupo(g.id, placares);
    out[g.id] = {};
    tab.forEach((l) => {
      out[g.id][l.timeId] = l.posicao;
    });
  });
  return out;
}

/**
 * DEPRECATED: Mantida apenas para compatibilidade com palpites antigos.
 * O bônus de +2 por grupo foi removido das regras a pedido do organizador
 * (passou a usar apenas placar exato 3 pts + acerto vencedor/empate 1 pt).
 * Sempre retorna 0.
 */
export function pontosPosicoesGrupos(
  _palpites: PlacaresMap,
  _reais: PlacaresMap
): number {
  return 0;
}

export function pontuacaoParticipante(
  palpites: PlacaresMap,
  reais: PlacaresMap,
  jogos: JogoCopa[],
  jogosMataMata: JogoMataMata[] = JOGOS_MATA_MATA,
  vencedoresPenaltisMM: VencedoresPenaltisMMMap = {}
): {
  total: number;
  jogos: number;
  placarExato: number;
  resultado1x2: number;
  posicoes: number;
  mataMata: number;
  placarExatoMM: number;
  acertoVencedorMM: number;
  porFaseMM: Record<FaseMataMata, number>;
} {
  let detalheJogos = 0;
  let placarExato = 0;
  let resultado1x2 = 0;

  jogos.forEach((jogo) => {
    const pal = palpites[jogo.id];
    const real = reais[jogo.id];
    if (!placarValido(pal) || !placarValido(real)) return;
    const pts = pontosJogo(pal, real);
    detalheJogos += pts;
    if (pts === 3) placarExato += 1;
    else if (pts === 1) resultado1x2 += 1;
  });

  let mataMata = 0;
  let placarExatoMM = 0;
  let acertoVencedorMM = 0;
  const porFaseMM: Record<FaseMataMata, number> = {
    '16avos': 0,
    oitavas: 0,
    quartas: 0,
    semi: 0,
    terceiro: 0,
    final: 0,
  };
  jogosMataMata.forEach((jogo) => {
    const pal = palpites[jogo.id];
    const real = reais[jogo.id];
    if (!placarValido(pal) || !placarValido(real)) return;
    const pts = pontosJogoMataMata(pal, real, jogo.fase, vencedoresPenaltisMM[jogo.id]);
    mataMata += pts;
    porFaseMM[jogo.fase] += pts;
    const ref = PONTUACAO_POR_FASE[jogo.fase];
    if (pts === ref.placarExato) placarExatoMM += 1;
    else if (pts === ref.acertoVencedor) acertoVencedorMM += 1;
  });

  const posicoes = pontosPosicoesGrupos(palpites, reais);
  return {
    total: detalheJogos + posicoes + mataMata,
    jogos: detalheJogos,
    placarExato,
    resultado1x2,
    posicoes,
    mataMata,
    placarExatoMM,
    acertoVencedorMM,
    porFaseMM,
  };
}

export function grupoEstaCompleto(grupoId: GrupoCopaId, placares: PlacaresMap): boolean {
  return jogosDoGrupoId(grupoId).every((j) => placarValido(placares[j.id]));
}

const IDS_JOGOS_MM = new Set(JOGOS_MATA_MATA.map((j) => j.id));

/** Só chaves dos 72 jogos da fase de grupos. */
export function extrairPalpitesGrupos(palpites: PlacaresMap): PlacaresMap {
  const out: PlacaresMap = {};
  JOGOS_COPA_2026.forEach((j) => {
    const p = palpites[j.id];
    if (placarValido(p)) out[j.id] = p;
  });
  return out;
}

/** Só chaves dos 32 jogos do mata-mata. */
export function extrairPalpitesMM(palpites: PlacaresMap): PlacaresMap {
  const out: PlacaresMap = {};
  JOGOS_MATA_MATA.forEach((j) => {
    const p = palpites[j.id];
    if (placarValido(p)) out[j.id] = p;
  });
  return out;
}

export function contarPalpitesMMValidos(palpites: PlacaresMap): number {
  return JOGOS_MATA_MATA.filter((j) => placarValido(palpites[j.id])).length;
}

/**
 * Mescla palpites da planilha: grupos (linha fase=grupos) + mata-mata (linha fase=matamata).
 * Os IDs de jogos do MM substituem entradas antigas do mesmo participante.
 */
/** Normaliza placares vindos da planilha (números como string, etc.). */
export function normalizarPlacaresImportados(map: PlacaresMap): PlacaresMap {
  const out: PlacaresMap = {};
  Object.keys(map || {}).forEach((k) => {
    const p = map[k];
    if (!p || typeof p !== 'object') return;
    if (k.startsWith('__')) {
      out[k] = p;
      return;
    }
    const m = Number(p.mandante);
    const v = Number(p.visitante);
    if (Number.isFinite(m) && Number.isFinite(v) && m >= 0 && v >= 0) {
      out[k] = { mandante: m, visitante: v };
    }
  });
  return out;
}

/** Chave reservada que carrega a meta de auto-preenchimento (vem na linha de grupos). */
const META_AUTO_KEY = '__meta_auto__';

export function mesclarPalpitesGruposEMM(
  palpitesGrupos: PlacaresMap,
  palpitesMMPlanilha: PlacaresMap,
  palpitesExistentes: PlacaresMap = {}
): PlacaresMap {
  const next: PlacaresMap = { ...palpitesExistentes };
  Object.keys(palpitesGrupos).forEach((k) => {
    if (!k.startsWith('__')) next[k] = palpitesGrupos[k];
  });
  // Preserva a meta de auto-preenchimento (__meta_auto__) que vem na linha de
  // grupos. Sem isso, o painel perde a contagem exata e cai na estimativa.
  if (placarValido(palpitesGrupos[META_AUTO_KEY])) {
    next[META_AUTO_KEY] = palpitesGrupos[META_AUTO_KEY];
  }
  // Mata-mata: upsert por jogo. Não apaga palpites MM já baixados quando a linha
  // fase=matamata ainda não veio neste sync (evita sumir na aba Palpites).
  Object.keys(palpitesMMPlanilha).forEach((k) => {
    if (k.startsWith('__') || !IDS_JOGOS_MM.has(k)) return;
    const p = palpitesMMPlanilha[k];
    if (placarValido(p)) next[k] = p;
  });
  return next;
}
